import React from 'react';
import { MultiCombobox, Popover, PopoverContent, PopoverTrigger } from '@onward-delivery/design';
import { SlidersHorizontal } from '@onward-delivery/design/icons';

import { BADGES, ONBOARDING_RELATIONSHIPS, RELATIONSHIP_SUBTITLES } from '../constants';
import { NO_FILTERS } from '../utils';

const TYPE_OPTIONS = ONBOARDING_RELATIONSHIPS.map((value) => ({ value, label: RELATIONSHIP_SUBTITLES[value] }));

const FilterGroup = ({ label, options, value, onChange }) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold tracking-wide text-muted">{label.toUpperCase()}</span>
        <MultiCombobox
            options={options}
            value={value}
            onValueChange={onChange}
            placeholder={`All ${label.toLowerCase()}s`}
            searchPlaceholder={`Search ${label.toLowerCase()}s…`}
            emptyText={`No ${label.toLowerCase()}s found`}
        />
    </div>
);

/**
 * The onboarding view's filter: carrier type (left out when `typeOptions` is empty) and badge, applied client side to
 * every tab. It looks like the design `FiltersPopover`, built from its parts because that one always opens from the
 * button's left edge, and here the button sits at the card's right edge: this one opens leftward (`align="end"`).
 *
 * @param {{ filters: { types: string[], badges: string[] }, onChange: Function, badges?: object[],
 *   typeOptions?: { value: string, label: string }[] }} props
 */
const OnboardingFilters = ({ filters, onChange, badges = BADGES, typeOptions = TYPE_OPTIONS }) => {
    const activeCount = filters.types.length + filters.badges.length;
    const badgeOptions = badges.map((badge) => ({ value: badge.key, label: badge.label }));

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md border border-border bg-surface px-3 text-sm text-foreground transition-colors hover:bg-muted/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                    <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Filters
                    {activeCount > 0 ? (
                        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-medium text-primary-fg">
                            {activeCount}
                        </span>
                    ) : null}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-3">
                <div className="flex flex-col gap-3">
                    {typeOptions.length ? (
                        <FilterGroup
                            label="Type"
                            options={typeOptions}
                            value={filters.types}
                            onChange={(types) => onChange({ ...filters, types })}
                        />
                    ) : null}
                    <FilterGroup
                        label="Badge"
                        options={badgeOptions}
                        value={filters.badges}
                        onChange={(badges) => onChange({ ...filters, badges })}
                    />
                </div>
                <div className="flex justify-end border-t border-border pt-3">
                    <button
                        type="button"
                        onClick={() => onChange(NO_FILTERS)}
                        disabled={activeCount === 0}
                        className="text-sm text-muted transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                    >
                        Clear filters
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default OnboardingFilters;
